import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, TextField, Grid, Typography } from "@mui/material";
import { Country, State, City } from "country-state-city";
import {
  allowOnlyAlphabetsCityState,
  PreventPasteCityState,
  sanitizeBlockedUnicode,
} from "./index";

const defaultLabelSx = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: "var(--text-color)",
  opacity: 0.55,
  mb: 0.75,
};

/**
 * MUI Autocomplete implementation of Country, State, City cascading dropdowns
 * Supports 3 labeling modes:
 * - "floating": MUI floating label inside TextField border outline (e.g. in CompanyInformation)
 * - "typography": External Typography header above field (e.g. in IdentityVerificationAddress)
 * - "html": External <label className="text-secondary"> (e.g. in BeneficialOwners)
 */
export const MuiCountryStateCityFields = ({
  control,
  setValue,
  watch,
  errors = {},
  countryKey = "country",
  stateKey = "state",
  cityKey = "city",
  countryLabel = "Country *",
  stateLabel = "State *",
  cityLabel = "City *",
  disabled = false,
  fieldStyle = {},
  gridSize = { xs: 12, sm: 6, md: 4 },
  labelType = "floating", // "floating" | "typography" | "html"
  labelSx = {},
}) => {
  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const currentCountryName = watch(countryKey) || "";
  const currentStateName = watch(stateKey) || "";
  const currentCityName = watch(cityKey) || "";

  // Resolve Country Object
  const countryObj = useMemo(() => {
    if (!currentCountryName || currentCountryName === "Select any") return null;
    return (
      allCountries.find(
        (c) =>
          c.name.toLowerCase() === currentCountryName.toLowerCase() ||
          c.isoCode.toLowerCase() === currentCountryName.toLowerCase()
      ) || null
    );
  }, [allCountries, currentCountryName]);

  // Resolve States
  const states = useMemo(() => {
    if (!countryObj) return [];
    return State.getStatesOfCountry(countryObj.isoCode) || [];
  }, [countryObj]);

  const hasStates = states.length > 0;

  // Resolve State Object
  const stateObj = useMemo(() => {
    if (!currentStateName || !hasStates || currentStateName === "Select any") return null;
    return (
      states.find(
        (s) =>
          s.name.toLowerCase() === currentStateName.toLowerCase() ||
          s.isoCode.toLowerCase() === currentStateName.toLowerCase()
      ) || null
    );
  }, [states, currentStateName, hasStates]);

  // Resolve Cities
  const cities = useMemo(() => {
    if (!countryObj || !stateObj) return [];
    return City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode) || [];
  }, [countryObj, stateObj]);

  const hasCities = cities.length > 0;

  const isStateDisabled = disabled || !currentCountryName || currentCountryName === "Select any";
  const isCityDisabled =
    disabled ||
    !currentCountryName ||
    currentCountryName === "Select any" ||
    (hasStates && (!currentStateName || currentStateName === "Select any"));

  const isFloating = labelType === "floating";
  const isHtml = labelType === "html";
  const isTypography = labelType === "typography";

  return (
    <>
      {/* ── Country Field ── */}
      <Grid size={gridSize}>
        {isTypography && (
          <Typography sx={{ ...defaultLabelSx, ...labelSx }}>
            {countryLabel}
          </Typography>
        )}
        {isHtml && (
          <label className="text-secondary">{countryLabel}</label>
        )}
        <Controller
          name={countryKey}
          control={control}
          rules={{
            required: "Country is required",
            validate: (val) =>
              (val && val !== "Select any") || "Country is required",
          }}
          render={({ field }) => (
            <Autocomplete
              {...field}
              fullWidth
              disabled={disabled}
              options={allCountries}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name || ""
              }
              isOptionEqualToValue={(option, value) => {
                if (!value) return false;
                const valStr = typeof value === "string" ? value : value.name;
                return (
                  option.name.toLowerCase() === valStr?.toLowerCase() ||
                  option.isoCode.toLowerCase() === valStr?.toLowerCase()
                );
              }}
              value={countryObj || (field.value && field.value !== "Select any" ? field.value : null)}
              onChange={(_, data) => {
                const newCountryName = data ? data.name : "";
                field.onChange(newCountryName);
                setValue(stateKey, "");
                setValue(cityKey, "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  label={isFloating ? countryLabel : undefined}
                  placeholder={isFloating ? undefined : "Select Country"}
                  error={!!errors[countryKey]}
                  helperText={errors[countryKey]?.message}
                  sx={fieldStyle}
                />
              )}
            />
          )}
        />
      </Grid>

      {/* ── State Field ── */}
      <Grid size={gridSize}>
        {isTypography && (
          <Typography sx={{ ...defaultLabelSx, ...labelSx }}>
            {stateLabel}
          </Typography>
        )}
        {isHtml && (
          <label className="text-secondary">{stateLabel}</label>
        )}
        {hasStates ? (
          <Controller
            name={stateKey}
            control={control}
            rules={{
              required: "State is required",
              validate: (val) =>
                (val && val !== "Select any") || "State is required",
            }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                disabled={isStateDisabled}
                options={states}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name || ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  const valStr = typeof value === "string" ? value : value.name;
                  return (
                    option.name.toLowerCase() === valStr?.toLowerCase() ||
                    option.isoCode.toLowerCase() === valStr?.toLowerCase()
                  );
                }}
                value={stateObj || (field.value && field.value !== "Select any" ? field.value : null)}
                onChange={(_, data) => {
                  const newStateName = data ? data.name : "";
                  field.onChange(newStateName);
                  setValue(cityKey, "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label={isFloating ? stateLabel : undefined}
                    placeholder={
                      isFloating
                        ? undefined
                        : isStateDisabled
                        ? "Select Country first"
                        : "Select State / Province"
                    }
                    error={!!errors[stateKey]}
                    helperText={errors[stateKey]?.message}
                    sx={fieldStyle}
                  />
                )}
              />
            )}
          />
        ) : (
          <Controller
            name={stateKey}
            control={control}
            rules={{ required: "State is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="outlined"
                disabled={isStateDisabled}
                autoComplete="off"
                label={isFloating ? stateLabel : undefined}
                placeholder={
                  isFloating
                    ? undefined
                    : isStateDisabled
                    ? "Select Country first"
                    : "Enter State"
                }
                error={!!errors[stateKey]}
                helperText={errors[stateKey]?.message}
                onKeyDown={allowOnlyAlphabetsCityState}
                onPaste={PreventPasteCityState}
                onChange={(e) =>
                  field.onChange(sanitizeBlockedUnicode(e.target.value))
                }
                sx={fieldStyle}
              />
            )}
          />
        )}
      </Grid>

      {/* ── City Field ── */}
      <Grid size={gridSize}>
        {isTypography && (
          <Typography sx={{ ...defaultLabelSx, ...labelSx }}>
            {cityLabel}
          </Typography>
        )}
        {isHtml && (
          <label className="text-secondary">{cityLabel}</label>
        )}
        {hasCities ? (
          <Controller
            name={cityKey}
            control={control}
            rules={{
              required: "City is required",
              validate: (val) =>
                (val && val !== "Select any") || "City is required",
            }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                disabled={isCityDisabled}
                options={cities}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name || ""
                }
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  const valStr = typeof value === "string" ? value : value.name;
                  return (
                    option.name.toLowerCase() === valStr?.toLowerCase()
                  );
                }}
                value={
                  cities.find(
                    (c) =>
                      c.name.toLowerCase() ===
                      (field.value || "").toLowerCase()
                  ) ||
                  (field.value && field.value !== "Select any" ? field.value : null)
                }
                onChange={(_, data) => {
                  field.onChange(data ? data.name : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    label={isFloating ? cityLabel : undefined}
                    placeholder={
                      isFloating
                        ? undefined
                        : isCityDisabled
                        ? "Select State first"
                        : "Select City"
                    }
                    error={!!errors[cityKey]}
                    helperText={errors[cityKey]?.message}
                    sx={fieldStyle}
                  />
                )}
              />
            )}
          />
        ) : (
          <Controller
            name={cityKey}
            control={control}
            rules={{ required: "City is required" }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                variant="outlined"
                disabled={isCityDisabled}
                autoComplete="off"
                label={isFloating ? cityLabel : undefined}
                placeholder={
                  isFloating
                    ? undefined
                    : isCityDisabled
                    ? "Select State first"
                    : "Enter City"
                }
                error={!!errors[cityKey]}
                helperText={errors[cityKey]?.message}
                onKeyDown={allowOnlyAlphabetsCityState}
                onPaste={PreventPasteCityState}
                onChange={(e) =>
                  field.onChange(sanitizeBlockedUnicode(e.target.value))
                }
                sx={fieldStyle}
              />
            )}
          />
        )}
      </Grid>
    </>
  );
};

export default MuiCountryStateCityFields;
